/* The Metadata Copilot's mark: three stacked facets — layers of metadata — and nothing with a face.
 *
 * **Abstract on purpose.** The brief rules out a robot, a human, eyes or a smiling AI, and the design
 * direction already rules out brain imagery and "AI sparkle". A stack of facets says what the copilot
 * actually reads — the catalogue's layers: datasets, columns, scores, incidents — rather than claiming
 * a personality the feature does not have. It is drawn from the Overview's own tokens, so it changes
 * with the theme like everything around it rather than being an illustration pasted onto the page.
 *
 * **Three states, each quiet, all optional.** Idle breathes — a 3% swell over three seconds, slow
 * enough to read as alive rather than as a loading spinner. Busy turns the core facet while the
 * copilot's capabilities are being read. Signal pulses a ring once when the platform has something
 * waiting for a person. Under reduced motion it is a still image in every state, and nothing about
 * the card depends on the motion: the words beside it carry the meaning.
 *
 * Decorative throughout, so `aria-hidden`: the card's heading names it.
 */

import { gsap } from 'gsap';
import { useId, useLayoutEffect, useRef } from 'react';
import type { ReactElement } from 'react';

import { motionAllowed } from '@/shared/ui/motion';

export function CopilotMark({
  busy,
  signal,
}: {
  readonly busy: boolean;
  readonly signal: boolean;
}): ReactElement {
  const ref = useRef<SVGSVGElement>(null);
  /* `useId` contains colons, which are legal in an id and break inside `url(#…)` in some engines. */
  const gradientId = `lq-ov-mark-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;

  /* Idle: the whole mark breathes, and the top facet floats a fraction more than the rest, so the
     layers read as layers rather than as one flat shape scaling. */
  useLayoutEffect(() => {
    const svg = ref.current;
    if (svg === null || !motionAllowed()) return undefined;
    const context = gsap.context(() => {
      gsap.to('.lq-ov-mark__body', {
        scale: 1.03,
        transformOrigin: '50% 60%',
        duration: 3.2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
      gsap.to('.lq-ov-mark__top', {
        y: -1.2,
        duration: 3.2,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    }, svg);
    return () => {
      context.revert();
    };
  }, []);

  /* Busy: the core facet turns while something is being read. Stopped and reverted the moment the
     read settles, so a finished state never keeps spinning. */
  useLayoutEffect(() => {
    const svg = ref.current;
    if (!busy || svg === null || !motionAllowed()) return undefined;
    const context = gsap.context(() => {
      gsap.to('.lq-ov-mark__core', {
        rotation: 360,
        transformOrigin: '50% 50%',
        duration: 4,
        ease: 'none',
        repeat: -1,
      });
    }, svg);
    return () => {
      context.revert();
    };
  }, [busy]);

  /* Signal: one ring, once, when something starts waiting for a person. A ring that pulsed for ever
     would be a notification badge nobody can clear. */
  useLayoutEffect(() => {
    const svg = ref.current;
    if (!signal || svg === null || !motionAllowed()) return undefined;
    const context = gsap.context(() => {
      gsap.fromTo(
        '.lq-ov-mark__ring',
        { scale: 0.75, opacity: 0.55, transformOrigin: '50% 50%' },
        { scale: 1.45, opacity: 0, duration: 1.2, ease: 'power2.out', delay: 0.6 },
      );
    }, svg);
    return () => {
      context.revert();
    };
  }, [signal]);

  return (
    <svg
      ref={ref}
      className="lq-ov-mark"
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" className="lq-ov-mark__stop-a" />
          <stop offset="1" className="lq-ov-mark__stop-b" />
        </linearGradient>
      </defs>
      <circle className="lq-ov-mark__ring" cx="24" cy="24" r="20" />
      <g className="lq-ov-mark__body">
        {/* Bottom layer: the widest, palest — the catalogue everything else sits on. */}
        <path className="lq-ov-mark__base" d="M24 26.5l15 7.5-15 7.5-15-7.5z" />
        <path className="lq-ov-mark__mid" d="M24 19l15 7.5-15 7.5-15-7.5z" />
        <g className="lq-ov-mark__top">
          <path d="M24 6.5l15 7.5-15 7.5-15-7.5z" fill={`url(#${gradientId})`} />
          {/* The core is a square seen in the facet's plane: drawn upright and flattened by its
              group, so turning it reads as the facet's centre spinning flat rather than as a shape
              tumbling out of the layer it sits in. */}
          <g transform="translate(24 14) scale(1 0.54)">
            <path className="lq-ov-mark__core" d="M0 -6.5L6.5 0 0 6.5 -6.5 0z" />
          </g>
        </g>
      </g>
    </svg>
  );
}
